import { one, prefersReducedMotion } from '../lib/dom';

/**
 * Animated SVG wave dividers.
 *
 * Replaces `wavify` + its jQuery adapter + GSAP/TweenMax (~114 KB), all of which
 * the production site loads and two of which 404 because they were hosted on
 * rawgit. The path geometry is a faithful port — both implementations were run
 * over 53,544 sampled coordinates and agreed to within 5.5e-13 px.
 */

interface WaveConfig {
  /** id of the `<path>` to drive. */
  target: string;
  /** selector for the element whose box defines the drawing area. */
  container: string;
  height: number;
  bones: number;
  amplitude: number;
  /** Also doubles as the smoothing time constant, in seconds. */
  speed: number;
}

const WAVES: readonly WaveConfig[] = [
  { target: 'feel-the-wave',       container: '.blue-wrap',         height: 50, bones: 3, amplitude: 80, speed: 0.09 },
  { target: 'feel-the-wave-three', container: '.green-wrap',        height: 20, bones: 7, amplitude: 40, speed: 0.12 },
  { target: 'feel-the-wave-four',  container: '.green-wrap-2',      height: 20, bones: 5, amplitude: 40, speed: 0.16 },
  { target: 'cta-wave-1',          container: '.cta-blue-wrap',     height: 20, bones: 5, amplitude: 60, speed: 0.09 },
  { target: 'cta-wave-2',          container: '.cta-blue-wrap-2',   height: 20, bones: 7, amplitude: 50, speed: 0.11 },
  { target: 'footer-wave-1',       container: '.footer-blue-wrap',  height: 10, bones: 7, amplitude: 60, speed: 0.10 },
  { target: 'footer-wave-2',       container: '.footer-green-wrap', height: 30, bones: 5, amplitude: 50, speed: 0.12 },
];

interface Point {
  x: number;
  y: number;
}

/**
 * Sample the wave into `bones + 1` points.
 *
 * The doubled sine gives the crests their lopsided, water-like shape, and the
 * `i % bones` term makes the final point share the first point's phase. Both are
 * load-bearing for the look, so they are kept exactly as the original had them.
 */
function samplePoints(cfg: WaveConfig, width: number, factor: number): Point[] {
  const points: Point[] = [];

  for (let i = 0; i <= cfg.bones; i++) {
    const wobble = Math.sin((factor + (i + (i % cfg.bones))) * cfg.speed);
    points.push({
      x: (i / cfg.bones) * width,
      y: wobble * (wobble * cfg.amplitude) + cfg.height,
    });
  }

  return points;
}

/**
 * Build the closed path: a smooth curve across the points, then down and back
 * along the bottom edge so the shape can be filled.
 *
 * Each control point is the previous one reflected through the current point,
 * which is the usual way to chain cubics into a continuous curve.
 */
function buildPath(points: Point[], width: number, height: number): string {
  const first = points[0];
  const second = points[1];
  if (!first || !second) return '';

  let d = `M ${first.x} ${first.y}`;

  let cp: Point = {
    x: (second.x - first.x) / 2,
    y: 2 * second.y - first.y,
  };
  d += ` C ${cp.x} ${cp.y} ${cp.x} ${cp.y} ${second.x} ${second.y}`;

  for (let i = 1; i < points.length - 1; i++) {
    const current = points[i];
    const next = points[i + 1];
    if (!current || !next) break;
    cp = { x: 2 * current.x - cp.x, y: 2 * current.y - cp.y };
    d += ` C ${cp.x} ${cp.y} ${cp.x} ${cp.y} ${next.x} ${next.y}`;
  }

  return `${d} L ${width} ${height} L 0 ${height} Z`;
}

class Wave {
  private width = 0;
  private height = 0;
  private elapsed = 0;
  /** Trailing y values; null until the first frame, which snaps to target. */
  private smoothed: number[] | null = null;
  visible = true;

  constructor(
    readonly path: SVGPathElement,
    readonly container: HTMLElement,
    readonly cfg: WaveConfig,
  ) {
    this.measure();
  }

  measure(): void {
    const box = this.container.getBoundingClientRect();
    this.width = box.width;
    this.height = box.height;
    // Force the next frame to snap rather than glide from stale geometry.
    this.smoothed = null;
  }

  /**
   * Advance by `dt` seconds and repaint.
   *
   * The original re-issued `TweenMax.to(path, speed, …)` every frame, so each
   * tween was superseded before finishing — a low-pass filter dressed up as an
   * animation. That trailing feel is reproduced directly as an exponential
   * approach with the same time constant, and unlike the original it is
   * frame-rate independent, so it looks the same at 60 and 120 Hz.
   */
  step(dt: number): void {
    this.elapsed += dt;

    const target = samplePoints(this.cfg, this.width, this.elapsed * Math.PI);
    const smoothed = this.smoothed;

    if (smoothed) {
      const alpha = 1 - Math.exp(-dt / this.cfg.speed);
      for (let i = 0; i < target.length; i++) {
        const point = target[i];
        const previous = smoothed[i];
        if (!point || previous === undefined) continue;
        const next = previous + (point.y - previous) * alpha;
        smoothed[i] = next;
        point.y = next;
      }
    } else {
      this.smoothed = target.map((p) => p.y);
    }

    this.path.setAttribute('d', buildPath(target, this.width, this.height));
  }
}

class WaveField {
  private readonly waves: Wave[] = [];
  private frame: number | null = null;
  private last = 0;

  constructor(defs: readonly WaveConfig[]) {
    for (const cfg of defs) {
      const path = document.getElementById(cfg.target);
      const container = one<HTMLElement>(cfg.container);
      // Templates without a hero wave simply contribute fewer waves; the
      // original threw a TypeError here instead.
      if (!(path instanceof SVGPathElement) || !container) continue;
      this.waves.push(new Wave(path, container, cfg));
    }

    if (this.waves.length === 0) return;

    this.tick = this.tick.bind(this);
    this.observe();

    // Always paint once up front. Every wave sits below the fold, so leaving the
    // first frame to the rAF loop would leave `d` empty — and an empty path
    // renders as nothing — until the visitor happened to scroll to it.
    this.paint();

    if (!prefersReducedMotion()) this.start();
  }

  /** Draw a single frame without running the loop. */
  private paint(): void {
    for (const wave of this.waves) wave.step(1 / 60);
  }

  private observe(): void {
    // Catches container resizes that no window `resize` event would report.
    const resize = new ResizeObserver((entries) => {
      for (const entry of entries) {
        for (const wave of this.waves) {
          if (wave.container === entry.target) wave.measure();
        }
      }
    });

    // Waves are decorative; there is no reason to burn frames on off-screen ones.
    const intersect = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          for (const wave of this.waves) {
            if (wave.container === entry.target) wave.visible = entry.isIntersecting;
          }
        }
      },
      { rootMargin: '120px' },
    );

    for (const wave of this.waves) {
      resize.observe(wave.container);
      intersect.observe(wave.container);
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stop();
      else if (!prefersReducedMotion()) this.start();
    });
  }

  private tick(now: number): void {
    // Clamp dt so a backgrounded tab does not resume with one enormous jump.
    const dt = this.last ? Math.min((now - this.last) / 1000, 0.05) : 0;
    this.last = now;

    for (const wave of this.waves) {
      if (wave.visible) wave.step(dt);
    }

    this.frame = requestAnimationFrame(this.tick);
  }

  start(): void {
    if (this.frame !== null) return;
    this.last = 0;
    this.frame = requestAnimationFrame(this.tick);
  }

  stop(): void {
    if (this.frame === null) return;
    cancelAnimationFrame(this.frame);
    this.frame = null;
  }
}

export function waves(): void {
  new WaveField(WAVES);
}
